import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FEATURES } from '../config/features';
import './SharedFormStyles.css';
import './PublishPost.css';
import { CustomButton8, CustomInputBox1, CustomButton2, CustomTextArea, ModernFormSection, SegmentedToggle, ModernDropdown, ModernDateTimePicker, Checkbox1 } from './Templates.js';
import {
  FaArrowLeft,
  FaCheck,
  FaLocationArrow,
  FaSearchLocation,
  FaCarSide,
  FaClock,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaSpinner,
  FaUsers,
  FaCalendarDay,
  FaCalendarWeek,
  FaChair,
  FaCar,
  FaAngleDown,
  FaLock,
  FaFileAlt,
  FaRedoAlt,
  FaEuroSign
} from 'react-icons/fa';
import { supabase } from './data/supabaseClient';
import { AuthContext } from './Provider.js';
import GoogleMapsRoute from './GoogleMapsRoute';
import { generateRoutePoints } from '../utils/routeMatchingUtils';
import { getCoordinates } from '../utils/geocodingUtils';
import { storeRouteMapImage } from '../utils/storeRouteMap';
import { useTranslation } from 'react-i18next';
import { useToast } from './hooks/ToastContext';
import Spinner from './common/Spinner';
import WeekdaySelector from './common/WeekdaySelector';
import OccurrencePreview from './common/OccurrencePreview';

export default function PublishPost() {
  const { t } = useTranslation('common');
  const { user, profileLoading } = useContext(AuthContext);
  const [isCarsLoading, setIsCarsLoading] = useState(true);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUserDataLoading, setIsUserDataLoading] = useState(true);
  const [cars, setCars] = useState([]);
  const [postLimits, setPostLimits] = useState(null);
  const [advanceBookingLimits, setAdvanceBookingLimits] = useState(null);
  const [dateValidationError, setDateValidationError] = useState(null);
  const navigate = useNavigate();
  const mapComponentRef = useRef();
  const { addToast } = useToast();

  // Separate state for dropdowns to avoid conflicts with the form state
  const [showSeatsMenu, setShowSeatsMenu] = useState(false);
  const [showCarsMenu, setShowCarsMenu] = useState(false);

  const [formData, setFormData] = useState({
    isPassenger: true,
    travelType: 'one-time', // 'one-time' or 'recurring'
    selectedSeat: 1,
    address_from: {
      street: '',
      city: '',
      country: ''
    },
    address_to: {
      street: '',
      city: '',
      country: ''
    },
    selectedCar: '',
    acceptsPackages: false,
    selectedDate: new Date(),
    selectedTime: new Date(),
    static_map_url: null,
    distance_km: null,
    description: '',
    error: null,
    // Recurring post fields
    selectedWeekdays: [], // Array of ISO weekday numbers (1-7)
    recurrenceEndDate: null, // End date for recurring posts
    // Price fields
    price: '',
    isFreeRide: false,
  });

  // Check if user has reached their post limit
  const hasReachedLimit = postLimits && !postLimits.has_remaining;

  // Check if there is an event ID in the URL parameters to associate the post with
  useEffect(() => {
    // Don't fetch event details if user has reached their limit
    if (hasReachedLimit) return;
    
    // Get the URL search parameters
    const queryParams = new URLSearchParams(window.location.search);
    const eventId = queryParams.get('event');
    
    if (eventId) {
      console.log(`Creating a ride for event ID: ${eventId}`);
      // Store the event ID in form data
      setFormData(prev => ({
        ...prev,
        eventId: eventId,
        // Default to driver for event rides
        isPassenger: false
      }));
      
      // Fetch event details to pre-fill location
      const fetchEventDetails = async () => {
        try {
          const { data, error } = await supabase
            .from('events')
            .select('location')
            .eq('id', eventId)
            .single();
            
          if (error) throw error;
          
          if (data) {
            // Set the destination to the event location
            setFormData(prev => ({
              ...prev,
              address_to: {
                street: data.location,
                city: '',
                country: ''
              }
            }));
          }
        } catch (err) {
          console.error('Error fetching event details:', err);
        }
      };
      
      fetchEventDetails();
    }
  }, [hasReachedLimit]);

  // Initialize component with user data
  useEffect(() => {
    const checkSession = async () => {
      try {
        setIsUserDataLoading(true);
        const { data } = await supabase.auth.getSession();
        if (!data.session) {
          navigate('/login');
          return;
        }
        
        // Check post limits
        if (data.session?.user) {
          const { data: limitData, error: limitError } = await supabase.rpc('check_monthly_post_limit', {
            p_user_id: data.session.user.id
          });
          
          if (limitError) {
            console.error('Error checking post limits:', limitError);
          } else if (limitData) {
            console.log('Post limits data:', limitData);
            setPostLimits(limitData);
          }

          // Check advance booking limits
          const { data: advanceLimitData, error: advanceLimitError } = await supabase.rpc('get_user_advance_booking_limits', {
            p_user_id: data.session.user.id
          });
          
          if (advanceLimitError) {
            console.error('Error checking advance booking limits:', advanceLimitError);
          } else if (advanceLimitData) {
            console.log('Advance booking limits data:', advanceLimitData);
            setAdvanceBookingLimits(advanceLimitData);
          }
        }
      } catch (error) {
        console.error("Error checking session:", error);
        addToast({
          type: 'error',
          title: t('errors.session_error'),
          description: t('errors.session_check_failed')
        });
        navigate('/login');
      } finally {
        setIsUserDataLoading(false);
      }
    };

    checkSession();
  }, [navigate, t, addToast]);

  // Fetch user's cars
  useEffect(() => {
    // Don't fetch cars if user has reached their limit
    if (hasReachedLimit || !user) return;
    
    const fetchCars = async () => {
      try {
        setIsCarsLoading(true);
        const { data, error } = await supabase
          .from('cars')
          .select('id, brand, model, number, accepts_packages')
          .eq('user_id', user.id);
  
        if (error) throw error;
        setCars(data || []);
      } catch (error) {
        console.error("Error fetching cars:", error);
        addToast({
          type: 'error',
          title: t('errors.data_load_error'),
          description: t('errors.cars_fetch_failed')
        });
      } finally {
        setIsCarsLoading(false);
      }
    };
  
    fetchCars();
  }, [user, t, addToast, hasReachedLimit]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleDocumentClick = (e) => {
      // Close dropdown menus when clicking outside
      if (showSeatsMenu && !e.target.closest('.PP-seats-selector')) {
        setShowSeatsMenu(false);
      }
      if (showCarsMenu && !e.target.closest('.PP-car-selector')) {
        setShowCarsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, [showSeatsMenu, showCarsMenu]);

  const handleBackClick = () => {
    navigate("/home");
  };

  const handleLocationSelect = (field, value) => {
    // Don't update form if user has reached their limit
    if (hasReachedLimit) return;
    
    console.log(`handleLocationSelect called with field: ${field}, value:`, value);
    
    if (field === 'distance_km') {
      setFormData(prev => ({
        ...prev,
        distance_km: value
      }));
    } else if (field === 'static_map_url') {
      setFormData(prev => ({
        ...prev,
        static_map_url: value
      }));
    } else if (field === 'route_polyline') {
      setFormData(prev => ({
        ...prev,
        route_polyline: value
      }));
      console.log('Route polyline saved to form state');
    }

    // Parse the Google Places result
    if (value && value.address_components) {
      const streetNumber = value.address_components.find(c => c.types.includes('street_number'))?.long_name || '';
      const route = value.address_components.find(c => c.types.includes('route'))?.long_name || '';

      // Check for special types (airports, train stations, etc.)
      const isAirport = value.types?.includes('airport');
      const isTransitStation = value.types?.includes('transit_station') || value.types?.includes('train_station');

      const establishment = value.address_components.find(c =>
        c.types.includes('establishment') ||
        c.types.includes('point_of_interest') ||
        c.types.includes('premise') ||
        c.types.includes('airport')
      )?.long_name || '';

      // Try to get city from address components with comprehensive fallback chain
      // Priority: locality → postal_town → sublocality → administrative_area_level_2 → administrative_area_level_1
      const cityTypes = [
        'locality',                    // Primary city name (e.g., "Rīga")
        'postal_town',                 // Postal town
        'sublocality',                 // Neighborhood/district (e.g., "Latgales priekšpilsēta")
        'administrative_area_level_2', // Municipality (e.g., "Mārupe")
        'administrative_area_level_1'  // Region fallback
      ];

      let city = '';
      for (const type of cityTypes) {
        const component = value.address_components.find(c => c.types.includes(type));
        if (component?.long_name) {
          city = component.long_name;
          break;
        }
      }

      const country = value.address_components.find(c => c.types.includes('country'))?.short_name || '';

      // Build street address intelligently
      let street;

      // For airports and transit stations, prefer the establishment name or formatted_address first part
      if (isAirport || isTransitStation) {
        if (value.name) {
          // Use the full place name for airports/stations (e.g., "Riga International Airport")
          street = value.name;
        } else if (establishment) {
          street = establishment;
        } else {
          // Fallback: take the first part of formatted_address before first comma
          street = value.formatted_address.split(',')[0].trim();
        }
      } else if (establishment && route) {
        // Establishment with street: "t/c Apelsīns, Rīgas gatve 5"
        street = streetNumber ? `${establishment}, ${route} ${streetNumber}` : `${establishment}, ${route}`;
      } else if (establishment) {
        // Just establishment: "t/c Apelsīns"
        street = establishment;
      } else if (streetNumber && route) {
        // Standard address: "Rīgas gatve 5"
        street = `${route} ${streetNumber}`;
      } else if (route) {
        // Just street name: "Rīgas gatve"
        street = route;
      } else {
        // Fallback to formatted address
        street = value.formatted_address;
      }

      // If city is still empty, try to extract it from formatted_address
      if (!city && value.formatted_address) {
        // formatted_address format: "Street, City, Country" or "Establishment, Street, City, Country"
        const parts = value.formatted_address.split(',').map(p => p.trim());

        // Try to find city by checking if part is not a number and not the country code
        for (let i = 1; i < parts.length - 1; i++) {
          const part = parts[i];
          // Skip if it's a number, too short, or looks like a street
          if (part && !/^\d+$/.test(part) && part.length > 2 && !part.match(/^(gatve|iela|ceļš)/i)) {
            // Check if this part contains any address components we already extracted
            const isStreetPart = (streetNumber && part.includes(streetNumber)) ||
                                (route && part === route) ||
                                (establishment && part === establishment);

            if (!isStreetPart) {
              city = part;
              break;
            }
          }
        }
      }

      const addressField = field === 'address_from' ? 'address_from' : 'address_to';

      console.log(`Setting ${addressField} to:`, {
        street,
        city,
        country,
        formatted_address: value.formatted_address
      });

      setFormData(prev => ({
        ...prev,
        [addressField]: {
          street,
          city,
          country
        }
      }));
    } else if (field === 'address_from' || field === 'address_to') {
      // If it's a direct address string (not a Google Place object)
      console.log(`Setting ${field} with string:`, value);
      
      // Extract what might be city/country from the string if possible
      const parts = typeof value === 'string' ? value.split(',').map(p => p.trim()) : [];
      
      setFormData(prev => ({
        ...prev,
        [field]: {
          street: typeof value === 'string' ? value : '',
          city: parts.length > 1 ? parts[1] : '',
          country: parts.length > 2 ? parts[2] : ''
        }
      }));
    }
  };

  // Price utility functions
  const normalizePriceInput = (value) => {
    if (!value) return '';
    // Replace comma with dot for consistent decimal separator
    return value.replace(',', '.');
  };

  const formatPriceValue = (value) => {
    if (!value) return '';
    const normalized = normalizePriceInput(value);
    const parsed = parseFloat(normalized);
    if (isNaN(parsed)) return '';
    // Format to 2 decimals
    return parsed.toFixed(2);
  };

  const isValidPriceInput = (value) => {
    if (!value) return true;
    const normalized = normalizePriceInput(value);
    // Allow: up to 3 digits before decimal, optional decimal with up to 2 digits after
    return /^\d{0,3}([.,]\d{0,2})?$/.test(value);
  };

  const handleChange = (field, value) => {
    // Don't update form if user has reached their limit
    if (hasReachedLimit) return;

    // Special handling for date changes - validate advance booking limits
    if (field === 'selectedDate' || field === 'selectedTime') {
      validateAdvanceBookingDate(field, value);
    }

    // Special handling for switching to recurring post type
    if (field === 'travelType' && value === 'recurring') {
      // Automatically set end date to max allowed date
      const maxDate = advanceBookingLimits?.max_date
        ? new Date(advanceBookingLimits.max_date)
        : null;

      setFormData(prev => ({
        ...prev,
        [field]: value,
        recurrenceEndDate: maxDate, // Auto-set to max allowed date
        error: null
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [field]: value,
      error: null // Clear any previous errors on field change
    }));
  };

  // Function to validate advance booking date limits
  const validateAdvanceBookingDate = async (field, value) => {
    if (!user || !advanceBookingLimits) return;

    // Get the complete date-time combination
    let checkDate, checkTime;
    if (field === 'selectedDate') {
      checkDate = value;
      checkTime = formData.selectedTime;
    } else if (field === 'selectedTime') {
      checkDate = formData.selectedDate;
      checkTime = value;
    }

    if (!checkDate || !checkTime) return;

    // Format date and time for the API call
    const travelDate = checkDate.toISOString().split('T')[0]; // YYYY-MM-DD format
    const travelTime = checkTime.toTimeString().split(' ')[0]; // HH:MM:SS format

    try {
      const { data: validationResult, error } = await supabase.rpc('check_advance_booking_limit', {
        p_user_id: user.id,
        p_travel_date: travelDate,
        p_travel_time: travelTime
      });

      if (error) {
        console.error('Error validating advance booking date:', error);
        return;
      }

      if (!validationResult.can_create) {
        setDateValidationError({
          message: validationResult.error,
          maxDays: validationResult.max_advance_days,
          userPlan: validationResult.user_plan,
          daysInAdvance: validationResult.days_in_advance
        });
      } else {
        setDateValidationError(null);
      }
    } catch (err) {
      console.error('Exception validating advance booking date:', err);
    }
  };

  const formatTimeString = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const validateForm = () => {
    // Don't validate if user has reached their limit
    if (hasReachedLimit) return false;

    // Check for advance booking date validation errors
    if (dateValidationError) {
      setFormData(prev => ({
        ...prev,
        error: dateValidationError.message
      }));
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: dateValidationError.message
      });
      return false;
    }

    console.log("Route polyline:", formData.route_polyline ? `${formData.route_polyline.substring(0, 30)}... (length: ${formData.route_polyline.length})` : 'Not set');
    console.log("Validating form with addresses:", formData.address_from, formData.address_to);

    // Properly check if both addresses are provided (both street fields must be non-empty)
    if (!formData.address_from.street || !formData.address_to.street) {
      console.log("Address validation failed: Missing street in from or to");
      setFormData(prev => ({
        ...prev,
        error: t('forms.address_required')
      }));
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: t('forms.address_required')
      });
      return false;
    }

    // Check if the route has been calculated by checking distance_km
    if (!formData.distance_km) {
      console.log("Route validation failed: Route not calculated");
      setFormData(prev => ({
        ...prev,
        error: t('forms.calculate_route')
      }));
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: t('forms.calculate_route')
      });
      return false;
    }

    // For driver posts, check if car is selected
    if (!formData.isPassenger && !formData.selectedCar) {
      console.log("Car validation failed: Driver without car selected");
      setFormData(prev => ({
        ...prev,
        error: t('forms.car_required')
      }));
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: t('forms.car_required')
      });
      return false;
    }

    // For recurring posts, validate weekdays and end date
    if (formData.travelType === 'recurring') {
      if (formData.selectedWeekdays.length === 0) {
        console.log("Recurring validation failed: No weekdays selected");
        setFormData(prev => ({
          ...prev,
          error: t('recurring.warning_select_weekdays') || 'Please select at least one weekday'
        }));
        addToast({
          type: 'error',
          title: t('errors.validation_error'),
          description: t('recurring.warning_select_weekdays') || 'Please select at least one weekday'
        });
        return false;
      }

      if (!formData.recurrenceEndDate) {
        console.log("Recurring validation failed: No end date set");
        setFormData(prev => ({
          ...prev,
          error: 'Please set an end date for recurring posts'
        }));
        addToast({
          type: 'error',
          title: t('errors.validation_error'),
          description: 'Please set an end date for recurring posts'
        });
        return false;
      }
    }

    console.log("Form validation passed");
    // Check if route polyline is captured
    if (!formData.route_polyline) {
      console.log("Route polyline validation failed: Polyline not captured");
      setFormData(prev => ({
        ...prev,
        error: t('forms.missing_route_data') || 'Route data missing. Please recalculate the route.'
      }));
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: t('forms.missing_route_data') || 'Route data missing. Please recalculate the route.'
      });
      return false;
    }

    return true;
  };

  const handlePublishPost = async () => {
    // Don't proceed if user has reached their limit
    if (hasReachedLimit) return;
    
    try {
      // Basic validation
      if (!validateForm()) {
        return;
      }

      setIsPublishing(true);
      
      // Check subscription limits for post publishing
      const { data: limitData, error: limitError } = await supabase.rpc('check_monthly_post_limit', {
        p_user_id: user.id
      });
      
      if (limitError) {
        console.error('Error checking post limits:', limitError);
        throw new Error('Could not verify your subscription limits');
      }
      
      if (!limitData.has_remaining) {
        const isDaily = limitData.limit_type === 'daily';
        addToast({
          type: 'error',
          title: t('errors.limit_reached'),
          description: isDaily
            ? t('forms.daily_post_limit_reached', { max: limitData.daily_max || 10 })
            : t('forms.post_limit_reached_description', { max: limitData.max_allowed })
        });

        // Only offer a plan upgrade for the (paywalled) monthly limit — never
        // for the daily anti-spam cap.
        if (!isDaily && FEATURES.PAYMENTS_ENABLED && window.confirm(t('package_forms.view_subscription_plans_confirm'))) {
          navigate('/subscription-plans');
          return;
        }

        setIsPublishing(false);
        setPostLimits(limitData);
        return;
      }
      
      // Update our local state with the fetched limits
      setPostLimits(limitData);
      
      // Geocode addresses to get coordinates for location alerts
      let fromCoords = null;
      let toCoords = null;
      
      try {
        // Get coordinates for both addresses
        const fromAddress = `${formData.address_from.street}, ${formData.address_from.city}, ${formData.address_from.country}`.trim();
        const toAddress = `${formData.address_to.street}, ${formData.address_to.city}, ${formData.address_to.country}`.trim();
        
        console.log('Geocoding FROM address:', fromAddress);
        const fromResult = await getCoordinates(fromAddress);
        
        if (fromResult.success) {
          fromCoords = fromResult.data;
          console.log('✅ FROM coordinates:', fromCoords);
        } else {
          console.warn('⚠️ Failed to geocode FROM address:', fromResult.error);
        }
        
        console.log('Geocoding TO address:', toAddress);
        const toResult = await getCoordinates(toAddress);
        
        if (toResult.success) {
          toCoords = toResult.data;
          console.log('✅ TO coordinates:', toCoords);
        } else {
          console.warn('⚠️ Failed to geocode TO address:', toResult.error);
        }
      } catch (geocodeError) {
        console.error('Geocoding error (non-critical):', geocodeError);
        // Don't block post creation if geocoding fails
        // Location alerts just won't work for this post
      }

      // Store the route map image once (in Supabase Storage) so the feed serves
      // our stored copy instead of re-billing Google Static Maps per card view.
      // Falls back to the Google URL if storage fails — no regression.
      const storedMapUrl = await storeRouteMapImage(formData.static_map_url, user.id);

      // Create post data
      const postData = {
        user_id: user.id,
        role: formData.isPassenger ? "Passenger" : "Driver",
        seats: formData.isPassenger ? null : formData.selectedSeat,
        car: formData.isPassenger ? null : formData.selectedCar,
        travel_date: formData.selectedDate,
        travel_time: formatTimeString(formData.selectedTime),
        travel_type: formData.travelType === 'recurring' ? 'recurring' : 'One-time',
        map_image_url: storedMapUrl,
        route_polyline: formData.route_polyline, // Add route polyline for matching
        search_radius_km: 10, // Default search radius of 10km
        distance_km: formData.distance_km,
        address_from_street: formData.address_from.street,
        address_from_city: formData.address_from.city,
        address_from_country: formData.address_from.country,
        address_to_street: formData.address_to.street,
        address_to_city: formData.address_to.city,
        address_to_country: formData.address_to.country,
        // Coordinates for location alerts (optional - post still works without them)
        from_latitude: fromCoords?.latitude || null,
        from_longitude: fromCoords?.longitude || null,
        from_place_id: fromCoords?.place_id || null,
        to_latitude: toCoords?.latitude || null,
        to_longitude: toCoords?.longitude || null,
        to_place_id: toCoords?.place_id || null,
        description: formData.description, // Add description field
        status: "active", // Ensure the post is active
        event_id: formData.eventId || null, // Associate with event if applicable
        // Price fields
        price: (!formData.isPassenger && formData.isFreeRide) ? null :
               (formData.price ? parseFloat(formData.price) : null),
        is_free: formData.isPassenger ? false : formData.isFreeRide,
        // Recurring post fields
        recurrence_pattern: formData.travelType === 'recurring' ? {
          type: 'weekly',
          weekdays: formData.selectedWeekdays,
          end_date: formData.recurrenceEndDate ? formData.recurrenceEndDate.toISOString().split('T')[0] : null
        } : null,
        recurrence_end_date: formData.travelType === 'recurring' && formData.recurrenceEndDate
          ? formData.recurrenceEndDate.toISOString().split('T')[0]
          : null
      };

      console.log("Publishing post with data:", postData);

      // Validate advance booking limits one more time before creating
      const travelDate = formData.selectedDate.toISOString().split('T')[0];
      const travelTime = formatTimeString(formData.selectedTime);
      
      const { data: advanceValidation, error: advanceError } = await supabase.rpc('check_advance_booking_limit', {
        p_user_id: user.id,
        p_travel_date: travelDate,
        p_travel_time: travelTime
      });
      
      if (advanceError) {
        console.error('Error validating advance booking limit:', advanceError);
        throw new Error('Could not validate booking date');
      }
      
      if (!advanceValidation.can_create) {
        throw new Error(advanceValidation.error);
      }

      // First create the post
      const { data: newPost, error: postError } = await supabase
        .from('posts')
        .insert(postData)
        .select()
        .single();
  
      if (postError) throw postError;

        // If we have a polyline, generate route points
        if (formData.route_polyline && newPost) {
          try {
            // Use our utility function to generate route points from the polyline
            const result = await generateRoutePoints(formData.route_polyline, {
              postId: newPost.id,
              packageId: null,
              intervalKm: 5 // Sample points every 5km
            });

            if (!result.success) {
              console.error('Error generating route points:', result.error);
              addToast({
                type: 'warning',
                title: 'Route Matching',
                description: 'Route matching may not work properly due to an issue with route data.'
              });
            } else {
              console.log('Route points generated successfully for post ID:', newPost.id, 'Count:', result.pointsCreated);
              addToast({
                type: 'success',
                title: 'Route Matching',
                description: `Route matching enabled with ${result.pointsCreated} route points.`
              });
            }
          } catch (routeError) {
            console.error('Exception in route points generation:', routeError);
            addToast({
              type: 'warning',
              title: 'Route Matching',
              description: 'Route matching may not work properly: ' + routeError.message
            });
          }
        }
  
      // Update user stats with explicit type conversion
      const { error: statsError } = await supabase.rpc('update_user_stats', {
        p_user_id: user.id,
        p_distance_offered: Number(formData.distance_km),
      });
  
      if (statsError) {
        console.error('Stats Error:', statsError);
        throw statsError;
      }
      
      // Increment the monthly post count
      const { data: incrementData, error: incrementError } = await supabase.rpc('increment_monthly_post_count', {
        p_user_id: user.id
      });
      
      if (incrementError) {
        console.error('Error incrementing post count:', incrementError);
        // Non-critical error, just log it
      } else if (incrementData) {
        // Update our local state with the new count
        setPostLimits(incrementData);
      }
      

  
      addToast({
        type: 'success',
        title: t('forms.publish_success'),
        description: t('forms.post_published', { role: postData.role })
      });

      // If associated with an event, add entry to event_rides table
      if (formData.eventId && !formData.isPassenger) {
        try {
          const eventRideData = {
            event_id: formData.eventId,
            post_id: newPost.id,
            driver_id: user.id,
            seats_offered: formData.selectedSeat,
            seats_available: formData.selectedSeat, // Initially all seats are available
            departure_location: formData.address_from.street,
            departure_time: new Date(
              formData.selectedDate.getFullYear(),
              formData.selectedDate.getMonth(),
              formData.selectedDate.getDate(),
              formData.selectedTime.getHours(),
              formData.selectedTime.getMinutes()
            ).toISOString()
          };
          
          const { error: eventRideError } = await supabase
            .from('event_rides')
            .insert(eventRideData);
            
          if (eventRideError) throw eventRideError;
          
          // Redirect to the event page instead of home
          navigate(`/events/${formData.eventId}`);
          return;
        } catch (eventRideError) {
          console.error('Error creating event ride:', eventRideError);
          // Continue with normal navigation even if this fails
        }
      }

      navigate('/home');
      
      // Show how many posts they have left this month
      if (incrementData && incrementData.max_allowed < 9999) {
        addToast({
          type: 'info',
          title: 'Post published',
          description: `You have ${incrementData.remaining} post${incrementData.remaining !== 1 ? 's' : ''} remaining this month.`
        });
      }
    } catch (error) {
      console.error('Error publishing post:', error);

      // Daily-cap hit at the DB trigger (safety net if the pre-check was raced).
      const isDailyCap = (error?.message || '').includes('DAILY_POST_LIMIT_REACHED');
      const description = isDailyCap
        ? t('forms.daily_post_limit_reached', { max: 10 })
        : (error.message || t('forms.publish_error'));

      setFormData(prev => ({ ...prev, error: description }));
      setIsPublishing(false);

      addToast({
        type: 'error',
        title: isDailyCap ? t('errors.limit_reached') : t('errors.error'),
        description
      });
    } finally {
      setIsPublishing(false);
    }
  };

  // Handle seat selection
  const handleSeatSelect = (seatNumber) => {
    // Don't update form if user has reached their limit
    if (hasReachedLimit) return;
    
    setFormData(prev => ({ ...prev, selectedSeat: seatNumber }));
    setShowSeatsMenu(false);
  };

  // Handle car selection
  const handleCarSelect = (car) => {
    // Don't update form if user has reached their limit
    if (hasReachedLimit) return;
    
    setFormData(prev => ({ 
      ...prev, 
      selectedCar: car
    }));
    setShowCarsMenu(false);
  };

  // Toggle dropdown states
  const toggleSeatsMenu = (e) => {
    // Don't toggle dropdowns if user has reached their limit
    if (hasReachedLimit) return;
    
    e.stopPropagation();
    setShowSeatsMenu(!showSeatsMenu);
    setShowCarsMenu(false); // Close the other dropdown
  };

  const toggleCarsMenu = (e) => {
    // Don't toggle dropdowns if user has reached their limit
    if (hasReachedLimit) return;
    
    e.stopPropagation();
    setShowCarsMenu(!showCarsMenu);
    setShowSeatsMenu(false); // Close the other dropdown
  };

  // Add loading spinner for initial loading or profile loading
  if (isUserDataLoading || profileLoading) {
    return (
      <div className="PP-loading-container">
        <Spinner size="medium" />
        <p>{profileLoading ? t('app.loading_profile') : t('app.loading')}</p>
      </div>
    );
  }

  if (isCarsLoading && !formData.isPassenger && !hasReachedLimit) {
    return (
      <div className="PP-loading-container">
        <Spinner size="medium" />
        <p>{t('app.loading_cars')}</p>
      </div>
    );
  }

  return (
    <div className={`PP-publish-post-container ${hasReachedLimit ? 'PP-limit-reached' : ''}`}>
        <div className="PP-back-button">
          <CustomButton8 onClick={handleBackClick}>
            <FaArrowLeft />
          </CustomButton8>
        </div>

        <div className="PP-form-header">
          <h2 className="PP-form-heading">{t('forms.publish_new_post')}</h2>
          <p className="PP-form-subtitle">{t('forms.share_journey_subtitle')}</p>
        </div>

        <div className="PP-form-content">

        {/* Daily cap notice — shown even while payments are paused (applies to all). */}
        {postLimits?.limit_type === 'daily' && (
          <div className="PP-post-limits-info PP-limit-reached">
            <FaExclamationTriangle />
            <span>{t('forms.daily_post_limit_reached', { max: postLimits.daily_max || 10 })}</span>
          </div>
        )}

        {/* Post Limits Info — hidden while payments are paused (everyone unlimited) */}
        {FEATURES.PAYMENTS_ENABLED && postLimits && (
          <div className={`PP-post-limits-info ${postLimits.has_remaining ? '' : 'PP-limit-reached'}`}>
            {postLimits.max_allowed >= 9999 ? (
              <span>{t('forms.unlimited_posts_subscription')}</span>
            ) : (
              <>
                <span>
                  {t('forms.posts_remaining_count', { remaining: postLimits.remaining, max: postLimits.max_allowed })}
                </span>
                {postLimits.next_reset_date && (
                  <span className="reset-date">{t('package_forms.resets_on', { date: new Date(postLimits.next_reset_date).toLocaleDateString() })}</span>
                )}
                {!postLimits.has_remaining && (
                  <div className="PP-upgrade-button-container">
                    <CustomButton2
                      label={t('forms.upgrade_plan_button')}
                      onClick={() => navigate('/subscription-plans')}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Advance Booking Limits Info — mentions the plan, hidden while payments paused */}
        {FEATURES.PAYMENTS_ENABLED && advanceBookingLimits && (
          <div className="PP-advance-booking-info">
            <div className="PP-info-header">
              <FaCalendarAlt />
              <span>{t('package_forms.advance_booking_limit')}</span>
            </div>
            <div className="PP-info-content">
              <span>{t('package_forms.advance_booking_description', {
                plan: advanceBookingLimits.plan_name || 'Basic',
                days: advanceBookingLimits.max_advance_days || 6
              })}</span>
              <span className="PP-max-date">{t('package_forms.maximum_date', { date: new Date(advanceBookingLimits.max_date).toLocaleDateString() })}</span>
            </div>
          </div>
        )}

        {/* Display limit reached overlay */}
        {hasReachedLimit && (
          <div className="PP-limit-overlay">
            <div className="PP-limit-overlay-content">
              <FaLock className="limit-overlay-icon" />
              <h3 className="PP-limit-overlay-title">{t('package_forms.monthly_limit_reached')}</h3>
              <p className="PP-limit-overlay-text">
                {t('forms.used_all_posts', { max: postLimits.max_allowed })}
                {' '}
                {t('forms.upgrade_or_wait', { date: new Date(postLimits.next_reset_date).toLocaleDateString() })}
              </p>
              <CustomButton2
                label={t('forms.upgrade_plan_button')}
                onClick={() => navigate('/subscription-plans')}
              />
            </div>
          </div>
        )}

        {formData.error && (
          <div className="PP-error-message">
            <FaExclamationTriangle />
            <span>{formData.error}</span>
          </div>
        )}

        <ModernFormSection
          icon={<FaUsers />}
          title={t('forms.details')}
        >
          <div className="PP-switches-container">
            {/* Travel Type: One-time vs Recurring */}
            <SegmentedToggle
              value={formData.travelType === 'recurring' ? 'recurring' : 'one-time'}
              onChange={(v) => handleChange('travelType', v)}
              disabled={hasReachedLimit}
              options={[
                { value: 'one-time', label: t('forms.one_time_ride'), icon: <FaCalendarDay /> },
                { value: 'recurring', label: t('forms.daily_ride'), icon: <FaRedoAlt /> }
              ]}
            />

            {/* Role: Driver vs Passenger */}
            <SegmentedToggle
              value={formData.isPassenger ? 'passenger' : 'driver'}
              onChange={(v) => handleChange('isPassenger', v === 'passenger')}
              disabled={hasReachedLimit}
              options={[
                { value: 'driver', label: t('posts.driver'), icon: <FaCarSide /> },
                { value: 'passenger', label: t('posts.passenger'), icon: <FaUsers /> }
              ]}
            />
          </div>

          {/* Recurring Post Options */}
          {formData.travelType === 'recurring' && (
            <div className="PP-recurring-options">
              <WeekdaySelector
                selectedWeekdays={formData.selectedWeekdays}
                onChange={(weekdays) => handleChange('selectedWeekdays', weekdays)}
                disabled={hasReachedLimit}
              />

              <div className="PP-recurring-end-date">
                <label>{t('forms.end_date') || 'End Date'}</label>
                <DatePicker
                  selected={formData.recurrenceEndDate}
                  onChange={(date) => handleChange('recurrenceEndDate', date)}
                  minDate={formData.selectedDate || new Date()}
                  maxDate={advanceBookingLimits ? new Date(advanceBookingLimits.max_date) : null}
                  dateFormat="yyyy-MM-dd"
                  placeholderText={t('forms.select_end_date') || 'Select end date'}
                  disabled={hasReachedLimit}
                  className="PP-date-picker"
                />
              </div>

              {/* Occurrence Preview */}
              {formData.selectedWeekdays.length > 0 && formData.recurrenceEndDate && (
                <OccurrencePreview
                  weekdays={formData.selectedWeekdays}
                  startDate={formData.selectedDate}
                  endDate={formData.recurrenceEndDate}
                  advanceLimit={advanceBookingLimits?.max_advance_days || 6}
                  userPlan={advanceBookingLimits?.plan_name || 'Basic'}
                />
              )}
            </div>
          )}

          {!formData.isPassenger && (
            <div className="PP-additional-options">
              <ModernDropdown
                label={t('forms.free_seats')}
                value={formData.selectedSeat}
                options={[1, 2, 3, 4, 5]}
                onSelect={handleSeatSelect}
                icon={<FaChair />}
                disabled={hasReachedLimit}
              />

              {cars.length === 0 ? (
                <div className="PP-no-cars">
                  <p>{t('forms.no_saved_cars')}</p>
                  <CustomButton2
                    label={t('forms.add_car_button')}
                    onClick={() => navigate('/MyprofileForm')}
                  >
                    <FaCar />
                  </CustomButton2>
                </div>
              ) : (
                <ModernDropdown
                  label={t('forms.car')}
                  value={formData.selectedCar}
                  options={cars.map(car => `${car.brand} ${car.model} - ${car.number}`)}
                  onSelect={(carString) => handleCarSelect(carString)}
                  icon={<FaCar />}
                  placeholder={t('forms.select_car')}
                  disabled={hasReachedLimit}
                />
              )}
            </div>
          )}
        </ModernFormSection>

        <ModernFormSection
          icon={<FaLocationArrow />}
          title={t('forms.travel_route')}
        >
          <GoogleMapsRoute
            ref={mapComponentRef}
            onLocationSelect={handleLocationSelect}
            disabled={hasReachedLimit}
          />
        </ModernFormSection>

        <ModernFormSection
          icon={<FaCalendarAlt />}
          title={t('forms.leaving_at')}
        >
          <ModernDateTimePicker
            dateValue={formData.selectedDate}
            timeValue={formData.selectedTime}
            onDateChange={date => handleChange('selectedDate', date)}
            onTimeChange={time => handleChange('selectedTime', time)}
            disabled={hasReachedLimit}
          />
          {/* Date validation error — inline with the field it refers to */}
          {dateValidationError && (
            <div className="PP-date-validation-error">
              <FaExclamationTriangle />
              <div className="PP-error-content">
                <p>{dateValidationError.message}</p>
                {FEATURES.PAYMENTS_ENABLED && (
                  <>
                    <p className="PP-error-details">
                      {t('forms.plan_allows_booking_days', { plan: dateValidationError.userPlan, maxDays: dateValidationError.maxDays })}
                      {' '}
                      {t('forms.tried_booking_days', { daysInAdvance: dateValidationError.daysInAdvance })}
                    </p>
                    <CustomButton2
                      label={t('forms.upgrade_plan_button')}
                      onClick={() => navigate('/subscription-plans')}
                    />
                  </>
                )}
              </div>
            </div>
          )}
        </ModernFormSection>

        <ModernFormSection
          icon={<FaFileAlt />}
          title={t('forms.description')}
        >
          <div className="PP-description-container">
            <CustomTextArea
              className="wsform-input"
              placeholder={t('forms.description_placeholder')}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              maxLength={300}
              rows={3}
              disabled={hasReachedLimit}
            />
          </div>
        </ModernFormSection>

        {/* Price Section */}
        <ModernFormSection
          icon={<FaEuroSign />}
          title={formData.isPassenger ? t('forms.price_offered') : t('forms.price_section_driver')}
        >
          {/* Free Ride Checkbox - Only for Drivers */}
          {!formData.isPassenger && (
            <div className="PP-free-ride-wrapper" style={{ marginBottom: '12px' }}>
              <Checkbox1
                id="isFreeRide"
                label={t('forms.free_ride_checkbox')}
                checked={formData.isFreeRide}
                onChange={(e) => {
                  handleChange('isFreeRide', e.target.checked);
                  // Clear price when free is checked
                  if (e.target.checked) {
                    handleChange('price', '');
                  }
                }}
              />
            </div>
          )}

          {/* Price Input */}
          <div className="PP-price-input-wrapper">
            <CustomInputBox1
              className="wsform-input"
              placeholder={formData.isPassenger ?
                t('forms.price_placeholder_passenger') :
                t('forms.price_placeholder_driver')}
              value={formData.price}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || isValidPriceInput(value)) {
                  handleChange('price', normalizePriceInput(value));
                }
              }}
              onBlur={(e) => {
                // Auto-format to 2 decimals on blur
                const formatted = formatPriceValue(e.target.value);
                handleChange('price', formatted);
              }}
              type="text"
              inputMode="decimal"
              disabled={hasReachedLimit || (!formData.isPassenger && formData.isFreeRide)}
            />
            <p className="PP-field-description" style={{ marginTop: '8px', fontSize: '0.9em', color: 'var(--secondaryColor)', opacity: 0.8 }}>
              {formData.isPassenger ?
                t('forms.price_description_passenger') :
                t('forms.price_description_driver')}
            </p>
          </div>
        </ModernFormSection>

        <div className="PP-publish-button-container">
          <CustomButton2
            label={isPublishing ? t('forms.publishing') : t('forms.publish_post')}
            onClick={handlePublishPost}
            disabled={isPublishing || hasReachedLimit}
          >
            {isPublishing ? <Spinner size="small" showText={false} /> : <FaCheck />}
          </CustomButton2>
          {hasReachedLimit && (
            <div className="PP-limit-message">
              <FaExclamationTriangle />
              <span>{t('forms.monthly_post_limit_reached')}</span>
            </div>
          )}
        </div>
        </div>
      </div>
  );
}